import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Tv, Smartphone, Radio, CalendarDays, Star, CheckCircle, Zap, Gem, BarChart2, Signal, ShoppingBag, Eye, Users, Lock, Share2, Layers, RefreshCw } from "lucide-react";

const CDN_B = "https://static-us-img.skywork.ai/prod/nexus/1781703216/";
const CDN_A = "https://static-us-img.skywork.ai/prod/nexus/1781703215/";
const IMGS = {
  fighter:      CDN_A + "cropped_image_0_1781703215896490734.jpg",
  scott:        "/scott-terrell.png",
  step1:        "/bkfc-box-set.png",
  step2:        "/fighter-step2.png",
  step3:        "/fighter-step3.png",
  step4:        "/holding-iphone-2.png",
  step5:        "/ipad-att.png",
  ar:           "/bkfc-triller.jpg",
  oldTV:        "/old-tv.png",
  newPhone:     "/bamboo-desk-att.png",
  mktEvent:     "/big-board.png",
  mktSocial:    "/fighter-market-social.png",
  mktStar:      "/fighter-market-star.png",
  sponsorship:  CDN_B + "cropped_image_8_1781703216482483968.jpg",
  analytics1:   "/analytics-dashboard.png",
  analytics2:   "/ipad-mockup-standing.png",
  brandAR:      "/ipad-att.png",
  brandFighter: "/fighter-profiles.png",
  brandAR2:     "/bkfc-triller.jpg",
  brandArtist:  "/artist-collector.png",
  card1:        CDN_A + "cropped_image_2_1781703215687084818.jpg",
  card2:        CDN_A + "cropped_image_3_1781703215779715647.jpg",
  card3:        CDN_A + "cropped_image_4_1781703215781695098.jpg",
  card4:        CDN_A + "cropped_image_5_1781703215784508513.jpg",
  card5:        CDN_A + "cropped_image_6_1781703215864628842.jpg",
  card6:        CDN_A + "cropped_image_7_1781703215980724368.jpg",
  card7:        CDN_A + "cropped_image_8_1781703215985461872.jpg",
  card8:        CDN_A + "cropped_image_9_1781703215988688349.jpg",
  card9:        CDN_A + "cropped_image_10_1781703215992703108.jpg",
  card10:       CDN_B + "cropped_image_11_1781703216067831623.jpg",
  fanEvent:     "/fan-event1.png",
  fanEvent2:    "/fan-event2.png",
  bkfcStats:    "/bkfc-stats.png",
  bkfcFight:    CDN_B + "cropped_image_12_1781703216711908032.jpg",
  mcgregor:     "/mcgregor.png",
};
const VIDEO_URL = "/fighter-video.mp4";
const NAV = [
  { label: "Overview", href: "#overview" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Revenue", href: "#revenue" },
  { label: "Sponsorship", href: "#sponsorship" },
  { label: "Analytics", href: "#analytics" },
  { label: "BKFC", href: "#bkfc" },
  { label: "Demo", href: "#demo" },
  { label: "The Ask", href: "#ask" },
];
const MUSIC_URL = "/spirit-of-4g.mp3";
const STEPS = [
  { num: "01", title: "Purchase The Pack or Cards", img: IMGS.step1, desc: "Our products are sold through traditional retail outlets (Walmart, Target, hobby shops, Breakers) and direct-to-consumer channels." },
  { num: "02", title: "Unwrap and Discover", img: IMGS.step2, desc: "Open your pack and locate the unique, embedded QR code on the back. You'll also find the artist's name displayed on the reverse side." },
  { num: "03", title: "Activate The Card", img: IMGS.step3, desc: "Scan the QR code to instantly activate your card, verify its authenticity, and add it to your secure digital collection." },
  { num: "04", title: "Engage Your Favorite Stars", img: IMGS.step4, desc: "Every Artist, Character & Talent has their own profile page featuring services that enable fans to engage directly with them." },
  { num: "05", title: "See Collection & Unlock Goodies", img: IMGS.step5, desc: "A digital version is added to your profile. When clicked, this activates all exclusive products, offers, experiences, prizes, and content." },
];
const PLACEMENTS = [
  { title: "First Inside Every Card", img: IMGS.brandAR, quote: "Your brand will be the first thing they see inside the card." },
  { title: "All 200 Fighter Profiles", img: IMGS.brandFighter, quote: "Your Ad will be on all 200 fighter profiles." },
  { title: "All AR Experiences", img: IMGS.brandAR2, quote: "Your Brand will appear on all AR experiences — clickable to purchase." },
  { title: "Artist & Collector Profiles", img: IMGS.brandArtist, quote: "Your Ad will be on all our artists' and collectors' profiles." },
];
const CARDS = [
  "/card-ipad-hand.png",
  "/card-mockup1.png",
  "/card-ar-ship.png",
  "/psd2.png",
  "/bamboo-desk-att.png",
  "/card-front-back.png",
  "/holding-iphone-2.png",
  "/iphone-mockup.png",
  "/psd1.png",
  "/packaging.png",
];
const BKFC_NUMS = [
  { val: "18,217", lbl: "KnuckleMania VI Fans" },
  { val: "2M+", lbl: "Viewers Per Event" },
  { val: "17M+", lbl: "Trading Cards Distributed" },
  { val: "$1.75M", lbl: "Founding Sponsor Ask" },
];

// ── Shared primitives ──────────────────────────────────────────
const fadeUp = { hidden:{opacity:0,y:36}, visible:{opacity:1,y:0,transition:{duration:0.65,ease:[0.22,1,0.36,1]}} };
const stagger = { hidden:{}, visible:{transition:{staggerChildren:0.1}} };

function useReveal() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref,{once:true,margin:"-80px 0px"});
  return {ref,inView};
}
function useIsMobile(bp=768) {
  const [mobile,setMobile] = useState(()=>typeof window!=="undefined"&&window.innerWidth<bp);
  useEffect(()=>{
    const mq=window.matchMedia(`(max-width:${bp-1}px)`);
    const h=(e:MediaQueryListEvent)=>setMobile(e.matches);
    mq.addEventListener("change",h);
    return ()=>mq.removeEventListener("change",h);
  },[bp]);
  return mobile;
}

function GoldDivider() {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,margin:"8px 0"}}>
      <div style={{height:1,flex:1,background:"linear-gradient(to right,transparent,#FFD234)"}}/>
      <div style={{width:6,height:6,background:"#FFD234",transform:"rotate(45deg)"}}/>
      <div style={{height:1,flex:1,background:"linear-gradient(to left,transparent,#FFD234)"}}/>
    </div>
  );
}

function Label({children}:{children:React.ReactNode}) {
  return <span style={{color:"#FFD234",fontFamily:"'JetBrains Mono',monospace",fontSize:"0.68rem",letterSpacing:"0.22em",textTransform:"uppercase"}}>{children}</span>;
}

function Heading({children,className=""}:{children:React.ReactNode;className?:string}) {
  return <h2 style={{fontFamily:"'Oswald',sans-serif",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.04em"}} className={`text-4xl md:text-5xl text-foreground ${className}`}>{children}</h2>;
}

function GoldBtn({children,href="#ask"}:{children:React.ReactNode;href?:string}) {
  return (
    <motion.a href={href}
      style={{background:"linear-gradient(135deg,#FFD234 0%,#CC9900 100%)",color:"#000",fontFamily:"'Oswald',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",padding:"14px 36px",borderRadius:4,display:"inline-block",cursor:"pointer",fontSize:"0.85rem",boxShadow:"0 4px 24px rgba(255,210,52,0.35)",textDecoration:"none"}}
      whileHover={{scale:1.04,boxShadow:"0 8px 36px rgba(255,210,52,0.5)"}}
      whileTap={{scale:0.97}}>
      {children}
    </motion.a>
  );
}

// ── Navbar ─────────────────────────────────────────────────────
function Navbar({scrolled}:{scrolled:boolean}) {
  return (
    <motion.nav initial={{y:-60,opacity:0}} animate={{y:0,opacity:1}} transition={{duration:0.6,ease:[0.22,1,0.36,1]}}
      style={{position:"fixed",top:0,left:0,right:0,zIndex:100,background:scrolled?"rgba(8,7,6,0.95)":"transparent",backdropFilter:scrolled?"blur(12px)":"none",borderBottom:scrolled?"1px solid rgba(255,210,52,0.12)":"none",transition:"all 0.4s",padding:"0 2rem"}}>
      <div style={{maxWidth:1200,margin:"0 auto",display:"flex",alignItems:"center",height:64,justifyContent:"space-between"}}>
        {/* @section: nav-logo — both logos always visible, including mobile */}
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <a href="#hero" style={{display:"flex",alignItems:"center",textDecoration:"none"}}>
            <img
              src="/yaas-logo.png"
              alt="Oh YAAS"
              style={{height:34,width:"auto",filter:"invert(1) sepia(1) saturate(2) hue-rotate(2deg) brightness(1.05)",objectFit:"contain"}}
            />
          </a>
          <div style={{width:1,height:26,background:"rgba(255,210,52,0.35)"}}/>
          <img
            src="/shipstation-logo.svg"
            alt="ShipStation Global"
            style={{height:18,width:"auto",objectFit:"contain",opacity:0.92}}
          />
        </div>
        {/* @section: nav-links — hidden on mobile, visible md+ */}
        <div className="hidden md:flex" style={{gap:"1.4rem",alignItems:"center"}}>
          {NAV.map(item=>(
            <a key={item.href} href={item.href}
              style={{color:"rgba(255,255,255,0.6)",fontSize:"0.75rem",fontFamily:"'Oswald',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",textDecoration:"none",transition:"color 0.2s"}}
              onMouseEnter={e=>(e.currentTarget.style.color="#FFD234")}
              onMouseLeave={e=>(e.currentTarget.style.color="rgba(255,255,255,0.6)")}>
              {item.label}
            </a>
          ))}
          <div style={{width:1,height:18,background:"rgba(255,210,52,0.3)"}}/>
          <span style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",fontSize:"0.75rem",letterSpacing:"0.12em",fontWeight:700}}>EXCLUSIVE</span>
        </div>
      </div>
    </motion.nav>
  );
}

// ── Hero ───────────────────────────────────────────────────────
function Hero({titleRef}:{titleRef:React.RefObject<HTMLDivElement>}) {
  const mobile = useIsMobile();
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    const attempt = () => { vid.play().catch(()=>{}); };
    attempt();
    // Safari sometimes needs a short delay after mount
    const t = setTimeout(attempt, 400);
    return () => clearTimeout(t);
  }, []);
  return (
    <section id="hero" style={{position:"relative",minHeight:"100vh",display:"flex",alignItems:"center",overflow:"hidden",background:"#080706"}}>
      {/* @section: hero-bg */}
      <div style={{position:"absolute",inset:0,zIndex:0}}>
        <img fetchPriority="high" decoding="async" src={IMGS.fighter} alt="BKFC Champion" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top",opacity:0.32}}/>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(to right,rgba(8,7,6,0.97) 38%,rgba(8,7,6,0.35) 100%)"}}/>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(to top,rgba(8,7,6,1) 0%,transparent 55%)"}}/>
      </div>
      {[0.15,0.44,0.78].map((x,i)=>(
        <motion.div key={i} initial={{scaleY:0,opacity:0}} animate={{scaleY:1,opacity:0.05}}
          transition={{duration:1.6,delay:i*0.25,ease:"easeOut"}}
          style={{position:"absolute",top:0,bottom:0,left:`${x*100}%`,width:1,background:"#FFD234",zIndex:1,transformOrigin:"top"}}/>
      ))}
      <div style={{position:"relative",zIndex:2,maxWidth:1200,margin:"0 auto",padding:"8rem 2rem 4rem",width:"100%"}}>
        <div style={{display:"flex",alignItems:"center",gap:mobile?"1.5rem":"4rem",flexWrap:"wrap"}}>
          {/* Left: text */}
          <div style={{flex:"1 1 300px",minWidth:0}}>
            {/* @section: hero-eyebrow */}
            <motion.div initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}} transition={{duration:0.6,delay:0.2}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:"1.5rem"}}>
                <div style={{height:1,width:40,background:"#FFD234"}}/>
                <Label>Confidential — ShipStation Global Partnership Proposal</Label>
              </div>
            </motion.div>
            {/* @section: hero-title */}
            <motion.div initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.35}}>
              <div ref={titleRef} style={{fontFamily:"'Oswald',sans-serif",fontWeight:700,fontSize:"clamp(3.2rem,8vw,6.5rem)",lineHeight:0.88,textTransform:"uppercase",letterSpacing:"-0.02em",marginBottom:"0.6rem",display:"inline-block",transition:"transform 55ms ease,text-shadow 55ms ease",transformOrigin:"left center",willChange:"transform"}}>
                <span style={{color:"#FFD234"}}>Oh YAAS</span><span style={{color:"white"}}>™</span>
              </div>
              <div style={{fontFamily:"'Oswald',sans-serif",fontWeight:400,fontSize:"clamp(1.1rem,2.5vw,1.8rem)",color:"rgba(255,255,255,0.5)",letterSpacing:"0.2em",textTransform:"uppercase",marginBottom:"2rem"}}>
                × BKFC × ShipStation Global
              </div>
            </motion.div>
            <motion.p initial={{opacity:0,y:18}} animate={{opacity:1,y:0}} transition={{duration:0.7,delay:0.5}}
              style={{fontFamily:"'Inter',sans-serif",color:"rgba(255,255,255,0.72)",fontSize:"1.1rem",lineHeight:1.75,marginBottom:"2.5rem",maxWidth:510}}>
              The first <em style={{color:"#FFD234",fontStyle:"normal",fontWeight:600}}>Experiential Collectible™</em> — combining the tangible appeal of physical trading cards with the immersive power of digital experiences.
            </motion.p>
            {/* @section: hero-ctas */}
            <motion.div initial={{opacity:0,y:18}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.65}}
              style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"center",marginBottom:"4rem"}}>
              <GoldBtn href="#ask">View The Ask — $1.75M</GoldBtn>
              <a href="#overview" style={{color:"rgba(255,255,255,0.5)",fontFamily:"'Oswald',sans-serif",fontSize:"0.82rem",letterSpacing:"0.12em",textTransform:"uppercase",textDecoration:"none",display:"flex",alignItems:"center",gap:8}}>
                <span style={{width:24,height:24,border:"1px solid rgba(255,210,52,0.4)",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.6rem",color:"#FFD234"}}>↓</span>
                Explore
              </a>
            </motion.div>
            {/* @section: hero-stats */}
            <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{duration:0.8,delay:1.1}}
              style={{display:"flex",gap:"2.5rem",flexWrap:"wrap"}}>
              {[["17M+","Trading Cards"],["18,217","KnuckleMania VI Fans"],["$1.75M","Founding Sponsor Ask"]].map(([v,l])=>(
                <div key={v}>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:"1.6rem",color:"#FFD234",fontWeight:700}}>{v}</div>
                  <div style={{fontSize:"0.7rem",color:"rgba(255,255,255,0.4)",letterSpacing:"0.1em",textTransform:"uppercase"}}>{l}</div>
                </div>
              ))}
            </motion.div>
          </div>
          {/* @section: hero-video */}
          <motion.div initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} transition={{duration:0.9,delay:0.5}}
            style={{flex:"0 0 auto",width:mobile?"100%":"clamp(280px,38vw,500px)",borderRadius:16,overflow:"hidden",border:"1px solid rgba(255,210,52,0.35)",boxShadow:"0 0 80px rgba(255,210,52,0.18)",alignSelf:"center"}}>
            <video ref={videoRef} src={VIDEO_URL} autoPlay muted loop playsInline preload="auto" style={{width:"100%",display:"block"}}/>
            <div style={{padding:"8px 14px",background:"rgba(8,7,6,0.92)",borderTop:"1px solid rgba(255,210,52,0.2)"}}>
              <p style={{fontSize:"0.68rem",color:"#FFD234",fontFamily:"'JetBrains Mono',monospace",margin:0}}>@kingkai_145 · BKFC Champion</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── Overview ───────────────────────────────────────────────────
function Overview() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  const [lbOpen,setLbOpen] = useState(false);
  return (
    <section id="overview" style={{padding:"7rem 2rem",background:"#0a0908"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto",display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:mobile?"2.5rem":"5rem",alignItems:"center"}}>
        {/* @section: overview-text */}
        <motion.div variants={fadeUp}>
          <Label>Company Overview</Label>
          <GoldDivider/>
          <Heading className="mt-4 mb-6">Oh YAAS Is Rewriting The Future Of Collecting</Heading>
          <p style={{color:"rgba(255,255,255,0.68)",lineHeight:1.8,marginBottom:"1.25rem"}}>
            For decades, trading cards stayed static. Oh YAAS transforms them into <strong style={{color:"#FFD234"}}>Experiential Collectibles™</strong> — living, interactive cards where every scan unlocks excitement, connection, and value.
          </p>
          <p style={{color:"rgba(255,255,255,0.68)",lineHeight:1.8,marginBottom:"2rem"}}>
            We bring fans closer to the stars they love, give collectors access to rare treasures, and open a powerful new marketing frontier for brands through data-rich, interactive engagement.
          </p>
          <div style={{padding:"1.5rem",border:"1px solid rgba(255,210,52,0.25)",borderRadius:8,background:"rgba(255,210,52,0.04)",marginBottom:"2rem"}}>
            <p style={{fontFamily:"'Oswald',sans-serif",fontSize:"1.3rem",color:"#FFD234",textTransform:"uppercase",letterSpacing:"0.08em",margin:0}}>
              "Scan it. Unlock it. Say Oh YAAS!"
            </p>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <img loading="lazy" decoding="async" src={IMGS.scott} alt="Scott Terrell" style={{width:60,height:60,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(255,210,52,0.4)"}}/>
            <div>
              <div style={{fontFamily:"'Oswald',sans-serif",color:"white",fontSize:"1rem",fontWeight:700}}>SCOTT TERRELL</div>
              <div style={{color:"#FFD234",fontSize:"0.72rem",letterSpacing:"0.1em",textTransform:"uppercase"}}>Founder &amp; President</div>
              <a href="mailto:scott@youareallstars.com" style={{color:"rgba(255,255,255,0.4)",fontSize:"0.76rem",textDecoration:"none"}}>scott@youareallstars.com</a>
            </div>
          </div>
        </motion.div>
        {/* @section: overview-image */}
        <motion.div variants={fadeUp} style={{position:"relative"}}>
          <div onClick={()=>setLbOpen(true)} style={{borderRadius:14,overflow:"hidden",border:"1px solid rgba(255,210,52,0.2)",boxShadow:"0 24px 64px rgba(0,0,0,0.55)",cursor:"zoom-in",position:"relative"}}>
            <img loading="lazy" decoding="async" src={IMGS.ar} alt="AR Experience" style={{width:"100%",display:"block"}}/>
            <div style={{position:"absolute",top:10,right:10,background:"rgba(8,7,6,0.75)",border:"1px solid rgba(255,210,52,0.4)",borderRadius:6,padding:"4px 10px",color:"#FFD234",fontSize:"0.68rem",fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.08em",backdropFilter:"blur(6px)"}}>⤢ Expand</div>
          </div>
          <div style={{position:"absolute",top:-18,right:-18,padding:"10px 18px",background:"rgba(8,7,6,0.9)",border:"1px solid rgba(255,210,52,0.3)",borderRadius:8,backdropFilter:"blur(8px)"}}>
            <p style={{fontFamily:"'JetBrains Mono',monospace",color:"#FFD234",fontSize:"0.72rem",margin:0}}>Scan The Front</p>
            <p style={{fontFamily:"'JetBrains Mono',monospace",color:"rgba(255,255,255,0.4)",fontSize:"0.63rem",margin:"2px 0 0"}}>Get a New AR Experience</p>
          </div>
        </motion.div>
      </motion.div>
      {/* Lightbox */}
      <AnimatePresence>
        {lbOpen && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            onClick={()=>setLbOpen(false)}
            style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem",cursor:"zoom-out"}}>
            <motion.div initial={{scale:0.88,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.88,opacity:0}}
              onClick={e=>e.stopPropagation()}
              style={{position:"relative",maxWidth:"90vw",maxHeight:"90vh",borderRadius:12,overflow:"hidden",border:"1px solid rgba(255,210,52,0.35)",cursor:"default"}}>
              <img src={IMGS.ar} alt="AR Experience" style={{display:"block",maxWidth:"90vw",maxHeight:"85vh",objectFit:"contain",background:"#080706"}}/>
              <button onClick={()=>setLbOpen(false)}
                style={{position:"absolute",top:12,right:12,width:36,height:36,borderRadius:"50%",background:"rgba(8,7,6,0.85)",border:"1px solid rgba(255,210,52,0.4)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── How It Works ───────────────────────────────────────────────
function HowItWorks() {
  const [active,setActive] = useState(0);
  const [lbOpen,setLbOpen] = useState(false);
  const [lbIdx,setLbIdx] = useState(0);
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();

  const openLb = (i:number) => { setLbIdx(i); setLbOpen(true); };
  const closeLb = () => setLbOpen(false);
  const prevLb = () => setLbIdx(i => (i-1+STEPS.length)%STEPS.length);
  const nextLb = () => setLbIdx(i => (i+1)%STEPS.length);

  useEffect(()=>{
    if(!lbOpen) return;
    const onKey = (e:KeyboardEvent) => {
      if(e.key==="ArrowLeft") prevLb();
      else if(e.key==="ArrowRight") nextLb();
      else if(e.key==="Escape") closeLb();
    };
    window.addEventListener("keydown",onKey);
    return () => window.removeEventListener("keydown",onKey);
  },[lbOpen]);

  return (
    <section id="how-it-works" style={{padding:"7rem 2rem",background:"#060504"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"4rem"}}>
          <Label>The Experience</Label>
          <GoldDivider/>
          <Heading className="mt-4">How It Works</Heading>
          <p style={{color:"rgba(255,255,255,0.5)",marginTop:"0.75rem",maxWidth:480,margin:"0.75rem auto 0"}}>Five steps from purchase to full immersive fan engagement</p>
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1.5fr",gap:"3rem",alignItems:"start"}}>
          {/* Step list */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {STEPS.map((step,i)=>(
              <button key={i} onClick={()=>setActive(i)}
                style={{textAlign:"left",padding:"1.1rem 1.4rem",borderRadius:8,border:"1px solid",cursor:"pointer",transition:"all 0.3s",background:"transparent",
                  borderColor:active===i?"rgba(255,210,52,0.45)":"rgba(255,255,255,0.06)",
                  backgroundColor:active===i?"rgba(255,210,52,0.07)":"transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:"0.72rem",color:active===i?"#FFD234":"rgba(255,255,255,0.22)",fontWeight:700}}>{step.num}</span>
                  <span style={{fontFamily:"'Oswald',sans-serif",fontSize:"1rem",color:active===i?"white":"rgba(255,255,255,0.5)",fontWeight:600,letterSpacing:"0.03em",textTransform:"uppercase"}}>{step.title}</span>
                </div>
                <AnimatePresence>
                  {active===i&&(
                    <motion.p key="d" initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}}
                      style={{color:"rgba(255,255,255,0.55)",fontSize:"0.83rem",lineHeight:1.65,marginTop:"0.65rem",marginLeft:"2rem",marginBottom:0}}>
                      {step.desc}
                    </motion.p>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
          {/* Image panel — click to expand */}
          <div style={{position:"sticky",top:100}}>
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{opacity:0,scale:0.97}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:1.02}}
                transition={{duration:0.4,ease:[0.22,1,0.36,1]}}
                style={{borderRadius:16,overflow:"hidden",border:"1px solid rgba(255,210,52,0.25)",boxShadow:"0 32px 80px rgba(0,0,0,0.6)",cursor:"pointer",position:"relative"}}
                onClick={()=>openLb(active)}
                whileHover={{scale:1.01,transition:{duration:0.2}}}>
                <div style={{position:"relative"}}>
                  <img loading="lazy" decoding="async" src={STEPS[active].img} alt={STEPS[active].title} style={{width:"100%",display:"block",aspectRatio:"4/3",objectFit:"cover"}}/>
                  {/* expand hint */}
                  <div style={{position:"absolute",top:12,right:12,width:34,height:34,borderRadius:8,background:"rgba(8,7,6,0.72)",backdropFilter:"blur(6px)",border:"1px solid rgba(255,210,52,0.35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",color:"#FFD234"}}>⤢</div>
                </div>
                <div style={{padding:"1.1rem 1.4rem",background:"rgba(6,5,4,0.96)",borderTop:"1px solid rgba(255,210,52,0.15)"}}>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",color:"#FFD234",fontSize:"0.68rem",letterSpacing:"0.16em"}}>STEP {STEPS[active].num}</div>
                  <div style={{fontFamily:"'Oswald',sans-serif",color:"white",fontSize:"1.05rem",textTransform:"uppercase",letterSpacing:"0.04em",marginTop:4}}>{STEPS[active].title}</div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lbOpen&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.25}}
            onClick={closeLb}
            style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(4,3,2,0.93)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem"}}>
            <motion.div initial={{opacity:0,scale:0.93,y:24}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95,y:16}}
              transition={{duration:0.35,ease:[0.22,1,0.36,1]}}
              onClick={e=>e.stopPropagation()}
              style={{position:"relative",maxWidth:860,width:"100%",borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,210,52,0.3)",boxShadow:"0 0 120px rgba(255,210,52,0.12)"}}>

              {/* Image */}
              <AnimatePresence mode="wait">
                <motion.img key={lbIdx} src={STEPS[lbIdx].img} alt={STEPS[lbIdx].title}
                  initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-40}}
                  transition={{duration:0.3,ease:[0.22,1,0.36,1]}}
                  style={{width:"100%",display:"block",maxHeight:"75vh",objectFit:"contain",background:"#080706"}}/>
              </AnimatePresence>

              {/* Info bar */}
              <div style={{padding:"1.4rem 1.8rem",background:"rgba(6,5,4,0.98)",borderTop:"1px solid rgba(255,210,52,0.18)"}}>
                <div style={{fontFamily:"'JetBrains Mono',monospace",color:"#FFD234",fontSize:"0.68rem",letterSpacing:"0.18em",marginBottom:"0.4rem"}}>STEP {STEPS[lbIdx].num} / 0{STEPS.length}</div>
                <div style={{fontFamily:"'Oswald',sans-serif",color:"white",fontSize:"1.3rem",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:"0.5rem"}}>{STEPS[lbIdx].title}</div>
                <p style={{color:"rgba(255,255,255,0.58)",fontSize:"0.88rem",lineHeight:1.7,margin:0}}>{STEPS[lbIdx].desc}</p>

                {/* Controls row */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"1.2rem"}}>
                  {/* Prev / Next */}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={prevLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"background 0.2s"}}
                      onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                      onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>←</button>
                    <button onClick={nextLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"background 0.2s"}}
                      onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                      onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>→</button>
                  </div>
                  {/* Dot indicators */}
                  <div style={{display:"flex",gap:7,alignItems:"center"}}>
                    {STEPS.map((_,i)=>(
                      <button key={i} onClick={()=>setLbIdx(i)}
                        style={{width:i===lbIdx?22:7,height:7,borderRadius:4,border:"none",cursor:"pointer",transition:"all 0.3s",background:i===lbIdx?"#FFD234":"rgba(255,255,255,0.2)",padding:0}}/>
                    ))}
                  </div>
                  {/* Close */}
                  <button onClick={closeLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.6)",fontSize:"1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}
                    onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,255,255,0.12)")}
                    onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,255,255,0.05)")}>✕</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── Old vs New ─────────────────────────────────────────────────
function OldVsNew() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  return (
    <section id="revenue" style={{padding:"7rem 2rem",background:"#0a0908"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"4rem"}}>
          <Label>Revenue Model</Label><GoldDivider/>
          <Heading className="mt-4">Old vs. New</Heading>
          <p style={{color:"rgba(255,255,255,0.5)",marginTop:"0.75rem"}}>Transforming a one-time sale into a recurring, diversified ecosystem</p>
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:"2rem",marginBottom:"2.5rem"}}>
          <div style={{padding:"2rem",borderRadius:12,border:"1px solid rgba(255,255,255,0.08)",background:"rgba(255,255,255,0.02)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"1.25rem"}}>
              <Tv size={22} strokeWidth={1.5} color="rgba(255,255,255,0.4)"/>
              <div>
                <div style={{fontFamily:"'Oswald',sans-serif",color:"rgba(255,255,255,0.35)",textTransform:"uppercase",fontSize:"0.82rem",letterSpacing:"0.08em"}}>Old Model</div>
                <div style={{color:"rgba(255,255,255,0.55)",fontSize:"0.88rem"}}>Sell the Card, End the Story</div>
              </div>
            </div>
            <img loading="lazy" decoding="async" src={IMGS.oldTV} alt="Old Model" style={{width:"100%",borderRadius:8,marginBottom:"1.25rem",opacity:0.65}}/>
            <p style={{color:"rgba(255,255,255,0.42)",fontSize:"0.85rem",lineHeight:1.7,marginBottom:"1rem"}}>Revenue comes almost entirely from the initial card sale. Once sold, the transaction and the customer relationship ends. No ongoing engagement, no recurring revenue.</p>
            <div style={{padding:"0.75rem 1rem",background:"rgba(255,255,255,0.03)",borderRadius:6,borderLeft:"3px solid rgba(255,255,255,0.1)"}}>
              <span style={{color:"rgba(255,255,255,0.3)",fontSize:"0.79rem"}}>⚠ One revenue source. No customer data. Static product.</span>
            </div>
          </div>
          <div style={{padding:"2rem",borderRadius:12,border:"1px solid rgba(255,210,52,0.3)",background:"rgba(255,210,52,0.04)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"1.25rem"}}>
              <Smartphone size={22} strokeWidth={1.5} color="#FFD234"/>
              <div>
                <div style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",textTransform:"uppercase",fontSize:"0.82rem",letterSpacing:"0.08em"}}>New Model</div>
                <div style={{color:"rgba(255,255,255,0.75)",fontSize:"0.88rem"}}>Sell the Card, Unlock the Ecosystem</div>
              </div>
            </div>
            <img loading="lazy" decoding="async" src={IMGS.newPhone} alt="New Model" style={{width:"100%",borderRadius:8,marginBottom:"1.25rem"}}/>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {["Digital Activation & Secure Resale","Premium Fan Services & Exclusive Content","Merchandise Sales & Event Access","Sponsorship & Brand Partnerships","Data & Insights Licensing"].map(s=>(
                <div key={s} style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:"#FFD234",flexShrink:0}}/>
                  <span style={{color:"rgba(255,255,255,0.68)",fontSize:"0.85rem"}}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
        <motion.div variants={fadeUp} style={{padding:"2rem",borderRadius:12,border:"1px solid rgba(255,210,52,0.2)",background:"rgba(255,210,52,0.04)",textAlign:"center"}}>
          <p style={{fontFamily:"'Oswald',sans-serif",fontSize:"1.35rem",color:"#FFD234",textTransform:"uppercase",letterSpacing:"0.06em",margin:0}}>
            The result: A recurring, diversified revenue model built on top of an already proven product category.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── Marketing ──────────────────────────────────────────────────
function Marketing() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  const [lbOpen,setLbOpen] = useState(false);
  const [lbIdx,setLbIdx] = useState(0);
  const cards = [
    {icon:<Radio size={18} strokeWidth={1.5} color="#FFD234"/>,title:"Breakers as Demand Engines",img:IMGS.mktEvent,desc:"Thousands of online Breakers livestream card openings to millions, acting as viral demand-drivers — often exhausting retail supply within hours."},
    {icon:<CalendarDays size={18} strokeWidth={1.5} color="#FFD234"/>,title:"Event Activations",img:IMGS.mktSocial,desc:"Live launches, fan experiences, and sponsorships tied to artists, leagues, and teams. Scaling through mass and specialty retail outlets."},
    {icon:<Star size={18} strokeWidth={1.5} color="#FFD234"/>,title:"Star Sites & Retail",img:IMGS.mktStar,desc:"Direct-to-fan sales through celebrity channels. Distribution already secured — positioned to launch at scale on day one."},
  ];

  const openLb = (i:number) => { setLbIdx(i); setLbOpen(true); };
  const closeLb = () => setLbOpen(false);
  const prevLb = () => setLbIdx(i => (i-1+cards.length)%cards.length);
  const nextLb = () => setLbIdx(i => (i+1)%cards.length);

  useEffect(()=>{
    if(!lbOpen) return;
    const onKey = (e:KeyboardEvent) => {
      if(e.key==="ArrowLeft") prevLb();
      else if(e.key==="ArrowRight") nextLb();
      else if(e.key==="Escape") closeLb();
    };
    window.addEventListener("keydown",onKey);
    return () => window.removeEventListener("keydown",onKey);
  },[lbOpen]);

  return (
    <section style={{padding:"7rem 2rem",background:"#060504"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"4rem"}}>
          <Label>Go-To-Market</Label><GoldDivider/>
          <Heading className="mt-4">How We Market These Cards</Heading>
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr 1fr",gap:"1.5rem",marginBottom:"2.5rem"}}>
          {cards.map((c,i)=>(
            <motion.div key={i} whileHover={{y:-5,borderColor:"rgba(255,210,52,0.4)"}}
              onClick={()=>openLb(i)}
              style={{borderRadius:12,overflow:"hidden",border:"1px solid rgba(255,210,52,0.12)",background:"rgba(255,255,255,0.01)",transition:"all 0.3s",cursor:"pointer"}}>
              <div style={{position:"relative"}}>
                <img loading="lazy" decoding="async" src={c.img} alt={c.title} style={{width:"100%",aspectRatio:"16/9",objectFit:"cover",display:"block"}}/>
                <div style={{position:"absolute",top:10,left:12,fontSize:"1.3rem"}}>{c.icon}</div>
                <div style={{position:"absolute",top:10,right:10,width:30,height:30,borderRadius:6,background:"rgba(8,7,6,0.72)",backdropFilter:"blur(6px)",border:"1px solid rgba(255,210,52,0.35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.85rem",color:"#FFD234"}}>⤢</div>
              </div>
              <div style={{padding:"1.2rem"}}>
                <div style={{fontFamily:"'Oswald',sans-serif",color:"white",fontSize:"0.95rem",textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:"0.5rem"}}>{c.title}</div>
                <p style={{color:"rgba(255,255,255,0.5)",fontSize:"0.83rem",lineHeight:1.65,margin:0}}>{c.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:"1rem"}}>
          {[[<CheckCircle size={20} strokeWidth={1.5} color="#FFD234"/>,"Distribution Secured","Already set to sell at scale."],[<Zap size={20} strokeWidth={1.5} color="#FFD234"/>,"Low-Cost, High-Impact","Viral demand via Breakers reduces CAC."],[<Gem size={20} strokeWidth={1.5} color="#FFD234"/>,"Scarcity Advantage","Limited supply builds pricing power."],[<BarChart2 size={20} strokeWidth={1.5} color="#FFD234"/>,"Capital Efficiency","PR is our primary category-leadership lever."]].map(([icon,t,d],idx)=>(
            <div key={idx} style={{padding:"1.2rem",borderRadius:8,border:"1px solid rgba(255,210,52,0.14)",background:"rgba(255,210,52,0.03)"}}>
              <div style={{fontSize:"1.3rem",marginBottom:"0.5rem"}}>{icon}</div>
              <div style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",fontSize:"0.88rem",textTransform:"uppercase",marginBottom:"0.4rem"}}>{t}</div>
              <p style={{color:"rgba(255,255,255,0.45)",fontSize:"0.78rem",lineHeight:1.6,margin:0}}>{d}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lbOpen&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.25}}
            onClick={closeLb}
            style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(4,3,2,0.93)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem"}}>
            <motion.div initial={{opacity:0,scale:0.93,y:24}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95,y:16}}
              transition={{duration:0.35,ease:[0.22,1,0.36,1]}}
              onClick={e=>e.stopPropagation()}
              style={{position:"relative",maxWidth:860,width:"100%",borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,210,52,0.3)",boxShadow:"0 0 120px rgba(255,210,52,0.12)"}}>
              <AnimatePresence mode="wait">
                <motion.img key={lbIdx} src={cards[lbIdx].img} alt={cards[lbIdx].title}
                  initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-40}}
                  transition={{duration:0.3,ease:[0.22,1,0.36,1]}}
                  style={{width:"100%",display:"block",maxHeight:"75vh",objectFit:"contain",background:"#080706"}}/>
              </AnimatePresence>
              <div style={{padding:"1.4rem 1.8rem",background:"rgba(6,5,4,0.98)",borderTop:"1px solid rgba(255,210,52,0.18)"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"0.4rem"}}>
                  <span style={{fontSize:"1.2rem"}}>{cards[lbIdx].icon}</span>
                  <div style={{fontFamily:"'Oswald',sans-serif",color:"white",fontSize:"1.3rem",textTransform:"uppercase",letterSpacing:"0.05em"}}>{cards[lbIdx].title}</div>
                </div>
                <p style={{color:"rgba(255,255,255,0.58)",fontSize:"0.88rem",lineHeight:1.7,margin:0}}>{cards[lbIdx].desc}</p>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"1.2rem"}}>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={prevLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                      onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                      onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>←</button>
                    <button onClick={nextLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                      onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                      onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>→</button>
                  </div>
                  <div style={{display:"flex",gap:7,alignItems:"center"}}>
                    {cards.map((_,i)=>(
                      <button key={i} onClick={()=>setLbIdx(i)}
                        style={{width:i===lbIdx?22:7,height:7,borderRadius:4,border:"none",cursor:"pointer",transition:"all 0.3s",background:i===lbIdx?"#FFD234":"rgba(255,255,255,0.2)",padding:0}}/>
                    ))}
                  </div>
                  <button onClick={closeLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.6)",fontSize:"1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,255,255,0.12)")}
                    onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,255,255,0.05)")}>✕</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── Sponsorship ────────────────────────────────────────────────
function Sponsorship() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  const [lbOpen,setLbOpen] = useState(false);
  const [lbIdx,setLbIdx] = useState(0);

  const openLb = (i:number) => { setLbIdx(i); setLbOpen(true); };
  const closeLb = () => setLbOpen(false);
  const prevLb = () => setLbIdx(i => (i-1+PLACEMENTS.length)%PLACEMENTS.length);
  const nextLb = () => setLbIdx(i => (i+1)%PLACEMENTS.length);

  useEffect(()=>{
    if(!lbOpen) return;
    const onKey = (e:KeyboardEvent) => {
      if(e.key==="ArrowLeft") prevLb();
      else if(e.key==="ArrowRight") nextLb();
      else if(e.key==="Escape") closeLb();
    };
    window.addEventListener("keydown",onKey);
    return () => window.removeEventListener("keydown",onKey);
  },[lbOpen]);
  return (
    <section id="sponsorship" style={{padding:"7rem 2rem",background:"#0a0908",position:"relative",overflow:"hidden"}} ref={ref as React.Ref<HTMLElement>}>
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:"60vw",height:"60vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(255,210,52,0.04) 0%,transparent 68%)",pointerEvents:"none"}}/>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto",position:"relative"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"4rem"}}>
          <Label>For ShipStation Global</Label><GoldDivider/>
          <Heading className="mt-4">Global Sponsorship Opportunity</Heading>
          <p style={{color:"rgba(255,255,255,0.5)",marginTop:"0.75rem",maxWidth:580,margin:"0.75rem auto 0"}}>
            Become the exclusive Global Sponsor of Oh YAAS and secure unparalleled visibility across BKFC — the fastest-growing combat sports organization in the world.
          </p>
        </motion.div>
        <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1.2fr 1fr",gap:"3rem",alignItems:"center",marginBottom:"3.5rem"}}>
          <motion.div variants={fadeUp}>
            <div style={{borderRadius:16,overflow:"hidden",border:"1px solid rgba(255,210,52,0.2)",boxShadow:"0 28px 72px rgba(0,0,0,0.5)",position:"relative",paddingBottom:"56.25%",height:0}}>
              <iframe
                src="https://www.youtube.com/embed/3AuEz4iZZvY?rel=0&modestbranding=1"
                title="Global Sponsorship Opportunity"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:"none"}}
              />
            </div>
            <p style={{color:"rgba(255,255,255,0.6)",fontSize:"0.88rem",lineHeight:1.8,marginTop:"1.25rem",padding:"1.1rem 1.3rem",borderRadius:10,border:"1px solid rgba(255,210,52,0.18)",background:"rgba(255,210,52,0.03)"}}>
              As the official sponsor of this commercial, your brand will receive prominent placement and recognition throughout the campaign. The commercial will air during BKFC events, giving your business exposure to as many as <strong style={{color:"#FFD234"}}>2 million viewers per fight</strong>. This partnership puts your brand directly in front of a passionate and rapidly growing audience, maximizing visibility and impact every time the commercial airs.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
            {[{n:"17M+",l:"Trading Cards",d:"Premium placement across all cards in the Oh YAAS ecosystem."},{n:"200+",l:"Fighter Profiles",d:"Your brand featured across every BKFC fighter, artist, and collector profile."},{n:"100%",l:"AR Experiences",d:"First prominent position in every Augmented Reality activation."},{n:"∞",l:"Real-Time Updates",d:"Continuously update content, promotions, and offers long after cards are distributed."}].map(it=>(
              <div key={it.n} style={{display:"flex",gap:16,padding:"1rem 1.2rem",borderRadius:8,border:"1px solid rgba(255,210,52,0.14)",background:"rgba(255,210,52,0.03)"}}>
                <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:"1.4rem",color:"#FFD234",fontWeight:700,minWidth:56}}>{it.n}</div>
                <div>
                  <div style={{fontFamily:"'Oswald',sans-serif",color:"white",fontSize:"0.88rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>{it.l}</div>
                  <div style={{color:"rgba(255,255,255,0.45)",fontSize:"0.8rem",marginTop:2,lineHeight:1.5}}>{it.d}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        {/* @section: placement-grid */}
        <motion.div variants={fadeUp}>
          <div style={{textAlign:"center",marginBottom:"1.5rem"}}><Label>Where Your Brand Lives Inside The Platform</Label></div>
          <div style={{display:"grid",gridTemplateColumns:mobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:"1.25rem"}}>
            {PLACEMENTS.map((p,i)=>(
              <motion.div key={i} whileHover={{y:-4,borderColor:"rgba(255,210,52,0.4)"}}
                onClick={()=>openLb(i)}
                style={{borderRadius:10,overflow:"hidden",border:"1px solid rgba(255,210,52,0.14)",background:"rgba(6,5,4,0.8)",transition:"all 0.3s",cursor:"pointer"}}>
                <div style={{position:"relative"}}>
                  <img loading="lazy" decoding="async" src={p.img} alt={p.title} style={{width:"100%",aspectRatio:"1",objectFit:"cover",display:"block"}}/>
                  <div style={{position:"absolute",inset:0,background:"linear-gradient(to top,rgba(6,5,4,0.75) 0%,transparent 55%)"}}/>
                  <div style={{position:"absolute",top:8,right:8,width:26,height:26,borderRadius:5,background:"rgba(8,7,6,0.72)",backdropFilter:"blur(4px)",border:"1px solid rgba(255,210,52,0.35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.72rem",color:"#FFD234",pointerEvents:"none"}}>⤢</div>
                </div>
                <div style={{padding:"1rem"}}>
                  <div style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",fontSize:"0.82rem",textTransform:"uppercase",marginBottom:"0.4rem"}}>{p.title}</div>
                  <p style={{color:"rgba(255,255,255,0.48)",fontSize:"0.77rem",lineHeight:1.6,margin:0,fontStyle:"italic"}}>"{p.quote}"</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lbOpen&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.25}}
            onClick={closeLb}
            style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(4,3,2,0.93)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem"}}>
            <motion.div initial={{opacity:0,scale:0.93,y:24}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95,y:16}}
              transition={{duration:0.35,ease:[0.22,1,0.36,1]}}
              onClick={e=>e.stopPropagation()}
              style={{position:"relative",maxWidth:860,width:"100%",borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,210,52,0.3)",boxShadow:"0 0 120px rgba(255,210,52,0.12)",background:"#080706"}}>
              <AnimatePresence mode="wait">
                <motion.img key={lbIdx} src={PLACEMENTS[lbIdx].img} alt={PLACEMENTS[lbIdx].title}
                  initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-40}}
                  transition={{duration:0.3,ease:[0.22,1,0.36,1]}}
                  style={{width:"100%",display:"block",maxHeight:"72vh",objectFit:"contain",background:"#080706"}}/>
              </AnimatePresence>
              <div style={{padding:"1.4rem 1.8rem",background:"rgba(6,5,4,0.98)",borderTop:"1px solid rgba(255,210,52,0.18)"}}>
                <div style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",fontSize:"1.2rem",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:"0.4rem"}}>{PLACEMENTS[lbIdx].title}</div>
                <p style={{color:"rgba(255,255,255,0.58)",fontSize:"0.88rem",lineHeight:1.7,margin:0,fontStyle:"italic"}}>"{PLACEMENTS[lbIdx].quote}"</p>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"1.2rem"}}>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={prevLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                      onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                      onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>←</button>
                    <button onClick={nextLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                      onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                      onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>→</button>
                  </div>
                  <div style={{display:"flex",gap:7,alignItems:"center"}}>
                    {PLACEMENTS.map((_,i)=>(
                      <button key={i} onClick={()=>setLbIdx(i)}
                        style={{width:i===lbIdx?22:7,height:7,borderRadius:4,border:"none",cursor:"pointer",transition:"all 0.3s",background:i===lbIdx?"#FFD234":"rgba(255,255,255,0.2)",padding:0}}/>
                    ))}
                  </div>
                  <button onClick={closeLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.6)",fontSize:"1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,255,255,0.12)")}
                    onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,255,255,0.05)")}>✕</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── Card Gallery ───────────────────────────────────────────────
function CardGallery() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  const [lbOpen,setLbOpen] = useState(false);
  const [lbIdx,setLbIdx] = useState(0);

  const openLb = (i:number) => { setLbIdx(i); setLbOpen(true); };
  const closeLb = () => setLbOpen(false);
  const prevLb = () => setLbIdx(i => (i-1+CARDS.length)%CARDS.length);
  const nextLb = () => setLbIdx(i => (i+1)%CARDS.length);

  useEffect(()=>{
    if(!lbOpen) return;
    const onKey = (e:KeyboardEvent) => {
      if(e.key==="ArrowLeft") prevLb();
      else if(e.key==="ArrowRight") nextLb();
      else if(e.key==="Escape") closeLb();
    };
    window.addEventListener("keydown",onKey);
    return () => window.removeEventListener("keydown",onKey);
  },[lbOpen]);

  return (
    <section style={{padding:"7rem 2rem",background:"#060504",overflow:"hidden"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"3rem"}}>
          <Label>Permanent Brand Placement</Label><GoldDivider/>
          <Heading className="mt-4">Your Brand Becomes The Collectible</Heading>
          <p style={{color:"rgba(255,255,255,0.5)",marginTop:"0.75rem",maxWidth:560,margin:"0.75rem auto 0"}}>
            Your logo featured across millions of trading cards — a collectible asset fans actively preserve for years to come.
          </p>
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"repeat(2,1fr)":"repeat(5,1fr)",gap:"0.75rem"}}>
          {CARDS.map((src,i)=>(
            <motion.div key={i}
              initial={{opacity:0,y:22}} animate={inView?{opacity:1,y:0}:{}} transition={{delay:i*0.065,duration:0.5}}
              whileHover={{scale:1.06,zIndex:10,boxShadow:"0 20px 50px rgba(255,210,52,0.3)"}}
              onClick={()=>openLb(i)}
              style={{borderRadius:8,overflow:"hidden",border:"1px solid rgba(255,210,52,0.14)",cursor:"pointer",position:"relative"}}>
              <img loading="lazy" decoding="async" src={src} alt={`Card ${i+1}`} style={{width:"100%",aspectRatio:"3/4",objectFit:"cover",display:"block"}}/>
              <div style={{position:"absolute",inset:0,background:"linear-gradient(to top,rgba(0,0,0,0.35) 0%,transparent 55%)",pointerEvents:"none"}}/>
              <div style={{position:"absolute",top:6,right:6,width:26,height:26,borderRadius:5,background:"rgba(8,7,6,0.72)",backdropFilter:"blur(4px)",border:"1px solid rgba(255,210,52,0.35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.72rem",color:"#FFD234",pointerEvents:"none"}}>⤢</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lbOpen&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.25}}
            onClick={closeLb}
            style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(4,3,2,0.93)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem"}}>
            <motion.div initial={{opacity:0,scale:0.93,y:24}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95,y:16}}
              transition={{duration:0.35,ease:[0.22,1,0.36,1]}}
              onClick={e=>e.stopPropagation()}
              style={{position:"relative",maxWidth:900,width:"100%",borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,210,52,0.3)",boxShadow:"0 0 120px rgba(255,210,52,0.12)",background:"#080706"}}>
              <AnimatePresence mode="wait">
                <motion.img key={lbIdx} src={CARDS[lbIdx]} alt={`Card ${lbIdx+1}`}
                  initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-40}}
                  transition={{duration:0.3,ease:[0.22,1,0.36,1]}}
                  style={{width:"100%",display:"block",maxHeight:"78vh",objectFit:"contain",background:"#080706"}}/>
              </AnimatePresence>
              {/* Controls bar */}
              <div style={{padding:"1rem 1.4rem",background:"rgba(6,5,4,0.98)",borderTop:"1px solid rgba(255,210,52,0.18)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={prevLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                    onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>←</button>
                  <button onClick={nextLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,210,52,0.35)",background:"rgba(255,210,52,0.07)",color:"#FFD234",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
                    onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,210,52,0.07)")}>→</button>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  {CARDS.map((_,i)=>(
                    <button key={i} onClick={()=>setLbIdx(i)}
                      style={{width:i===lbIdx?20:6,height:6,borderRadius:3,border:"none",cursor:"pointer",transition:"all 0.3s",background:i===lbIdx?"#FFD234":"rgba(255,255,255,0.2)",padding:0}}/>
                  ))}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontFamily:"'JetBrains Mono',monospace",color:"rgba(255,255,255,0.35)",fontSize:"0.68rem"}}>{lbIdx+1} / {CARDS.length}</span>
                  <button onClick={closeLb} style={{width:42,height:42,borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.6)",fontSize:"1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,255,255,0.12)")}
                    onMouseLeave={e=>(e.currentTarget.style.background="rgba(255,255,255,0.05)")}>✕</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ── Analytics ──────────────────────────────────────────────────
function Analytics() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  return (
    <section id="analytics" style={{padding:"7rem 2rem",background:"#0a0908"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"4rem"}}>
          <Label>Data &amp; Intelligence</Label><GoldDivider/>
          <Heading className="mt-4">Real-Time Analytics &amp; Consumer Intelligence</Heading>
          <p style={{color:"rgba(255,255,255,0.5)",marginTop:"0.75rem",maxWidth:580,margin:"0.75rem auto 0"}}>
            Unlike traditional sponsorships that leave brands guessing — Oh YAAS provides complete visibility that no trading card company has ever offered at scale.
          </p>
        </motion.div>
        <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:"2.5rem",alignItems:"start"}}>
          <motion.div variants={fadeUp} style={{display:"flex",flexDirection:"column",gap:"0.9rem"}}>
            {[{icon:<Signal size={18} strokeWidth={1.5} color="#FFD234"/>,label:"Card Activations & Scans",val:"Real-time"},{icon:<ShoppingBag size={18} strokeWidth={1.5} color="#FFD234"/>,label:"Consumer Purchases & Click-Through Rates",val:"Live tracking"},{icon:<Eye size={18} strokeWidth={1.5} color="#FFD234"/>,label:"AR Experience Interactions & Durations",val:"Immersive data"},{icon:<Users size={18} strokeWidth={1.5} color="#FFD234"/>,label:"Profile Visits & Engagement Metrics",val:"Deep insights"},{icon:<Lock size={18} strokeWidth={1.5} color="#FFD234"/>,label:"First-Party Customer Data Ownership",val:"You own it"},{icon:<Share2 size={18} strokeWidth={1.5} color="#FFD234"/>,label:"CRM & Marketing System Export",val:"Full integration"}].map(it=>(
              <div key={it.label} style={{display:"flex",alignItems:"center",gap:14,padding:"0.85rem 1.2rem",borderRadius:8,border:"1px solid rgba(255,210,52,0.11)",background:"rgba(255,210,52,0.02)"}}>
                <span style={{fontSize:"1.15rem"}}>{it.icon}</span>
                <div style={{flex:1,color:"rgba(255,255,255,0.75)",fontSize:"0.86rem"}}>{it.label}</div>
                <span style={{fontFamily:"'JetBrains Mono',monospace",color:"#FFD234",fontSize:"0.7rem",whiteSpace:"nowrap"}}>{it.val}</span>
              </div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
            <div style={{borderRadius:12,overflow:"hidden",border:"1px solid rgba(255,210,52,0.2)"}}>
              <img loading="lazy" decoding="async" src={IMGS.analytics1} alt="Analytics Dashboard" style={{width:"100%",display:"block"}}/>
            </div>
            <div style={{borderRadius:12,overflow:"hidden",border:"1px solid rgba(255,210,52,0.2)"}}>
              <img loading="lazy" decoding="async" src={IMGS.analytics2} alt="Consumer Intelligence" style={{width:"100%",display:"block"}}/>
            </div>
          </motion.div>
        </div>
        <motion.div variants={fadeUp} style={{padding:"2rem",borderRadius:12,border:"1px solid rgba(255,210,52,0.25)",background:"rgba(255,210,52,0.04)",textAlign:"center",marginTop:"2.5rem"}}>
          <p style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",fontSize:"1.05rem",textTransform:"uppercase",letterSpacing:"0.08em",margin:0}}>
            This level of transparency, attribution, and first-party data ownership is something no trading card company has ever offered at scale.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── Fan Engagement ─────────────────────────────────────────────
function FanEngagement() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  const [idx,setIdx] = useState(0);
  const imgs = [IMGS.fanEvent, IMGS.fanEvent2];
  const prev = () => setIdx(i=>(i-1+imgs.length)%imgs.length);
  const next = () => setIdx(i=>(i+1)%imgs.length);
  return (
    <section style={{padding:"7rem 2rem",background:"#060504"}} ref={ref as React.Ref<HTMLElement>}>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto",display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:mobile?"2.5rem":"4rem",alignItems:"center"}}>
        <motion.div variants={fadeUp}>
          {/* Image carousel */}
          <div style={{position:"relative",borderRadius:16,overflow:"hidden",border:"1px solid rgba(255,210,52,0.2)",boxShadow:"0 28px 72px rgba(0,0,0,0.5)"}}>
            <AnimatePresence mode="wait">
              <motion.img key={idx} loading="lazy" decoding="async" src={imgs[idx]} alt={`Fan Engagement ${idx+1}`}
                initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-40}}
                transition={{duration:0.35,ease:[0.22,1,0.36,1]}}
                style={{width:"100%",display:"block",objectFit:"cover"}}/>
            </AnimatePresence>
            {/* Prev / Next arrows */}
            <button onClick={prev} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",width:38,height:38,borderRadius:9,border:"1px solid rgba(255,210,52,0.4)",background:"rgba(8,7,6,0.75)",backdropFilter:"blur(6px)",color:"#FFD234",fontSize:"1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
              onMouseLeave={e=>(e.currentTarget.style.background="rgba(8,7,6,0.75)")}>←</button>
            <button onClick={next} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",width:38,height:38,borderRadius:9,border:"1px solid rgba(255,210,52,0.4)",background:"rgba(8,7,6,0.75)",backdropFilter:"blur(6px)",color:"#FFD234",fontSize:"1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,210,52,0.18)")}
              onMouseLeave={e=>(e.currentTarget.style.background="rgba(8,7,6,0.75)")}>→</button>
          </div>
          {/* Dot indicators */}
          <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:"1rem"}}>
            {imgs.map((_,i)=>(
              <button key={i} onClick={()=>setIdx(i)}
                style={{width:i===idx?22:8,height:8,borderRadius:4,border:"none",cursor:"pointer",transition:"all 0.3s",background:i===idx?"#FFD234":"rgba(255,255,255,0.2)",padding:0}}/>
            ))}
          </div>
        </motion.div>
        <motion.div variants={fadeUp}>
          <Label>Event Activations</Label><GoldDivider/>
          <Heading className="mt-4 mb-6">Sponsored Fan Engagement Night</Heading>
          <p style={{color:"rgba(255,255,255,0.68)",lineHeight:1.8,marginBottom:"1.25rem"}}>
            Every attendee receives a complimentary exclusive trading card pack featuring the fighters competing that evening. With BKFC events drawing <strong style={{color:"#FFD234"}}>17,000–18,000 fans</strong> and reaching between <strong style={{color:"#FFD234"}}>800K–2M viewers</strong> per event.
          </p>
          <p style={{color:"rgba(255,255,255,0.68)",lineHeight:1.8,marginBottom:"2rem"}}>
            Unlike traditional event giveaways, these collectible cards become lasting keepsakes that fans take home, trade, and continue to engage with — extending your sponsorship reach far beyond fight night.
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {["Collect consumer data at scale","Generate leads in real-time","Distribute rewards and create loyalty","Measure ROI from every single activation"].map(s=>(
              <div key={s} style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:18,height:18,borderRadius:"50%",border:"1.5px solid #FFD234",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:"#FFD234"}}/>
                </div>
                <span style={{color:"rgba(255,255,255,0.68)",fontSize:"0.9rem"}}>{s}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── BKFC Stats ─────────────────────────────────────────────────
function BKFCStats() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  return (
    <section id="bkfc" style={{padding:"7rem 2rem",background:"#080706",position:"relative",overflow:"hidden"}} ref={ref as React.Ref<HTMLElement>}>
      <div style={{position:"absolute",inset:0,backgroundImage:`url(${IMGS.bkfcFight})`,backgroundSize:"cover",backgroundPosition:"center",opacity:0.07}}/>
      <div style={{position:"absolute",inset:0,background:"rgba(8,7,6,0.93)"}}/>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1200,margin:"0 auto",position:"relative"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"4rem"}}>
          <Label>BKFC Growth</Label><GoldDivider/>
          <Heading className="mt-4">BKFC Key Data Points</Heading>
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:"1.5rem",marginBottom:"3rem"}}>
          {BKFC_NUMS.map((s,i)=>(
            <div key={i} style={{padding:"2rem",borderRadius:12,border:"1px solid rgba(255,210,52,0.22)",background:"rgba(255,210,52,0.04)",textAlign:"center"}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:"2rem",color:"#FFD234",fontWeight:700,marginBottom:"0.5rem"}}>{s.val}</div>
              <div style={{color:"rgba(255,255,255,0.5)",fontSize:"0.8rem",textTransform:"uppercase",letterSpacing:"0.08em",lineHeight:1.4}}>{s.lbl}</div>
            </div>
          ))}
        </motion.div>
        <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1.4fr 1fr",gap:"2.5rem",alignItems:"start"}}>
          <motion.div variants={fadeUp} style={{display:"flex",flexDirection:"column",gap:10}}>
            {["BKFC is one of the fastest-growing combat sports organizations in the world with explosive growth across live attendance, pay-per-view, and social media.","KnuckleMania VI broke its own record with 18,217 fans in attendance in 2026, surpassing previous UFC and boxing records.","Events regularly generate between 800K to over 2 million total viewers per event across television, streaming, and digital.","BKFC features former UFC champions and global stars, including partnerships with Conor McGregor, driving mainstream awareness.","Distributed globally through major streaming, pay-per-view, and broadcast partners reaching fans across North America, Europe, Latin America, Asia, and international markets."].map((b,i)=>(
              <div key={i} style={{display:"flex",gap:12,padding:"0.85rem 1.1rem",borderRadius:6,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:"#FFD234",marginTop:6,flexShrink:0}}/>
                <p style={{color:"rgba(255,255,255,0.62)",fontSize:"0.86rem",lineHeight:1.65,margin:0}}>{b}</p>
              </div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
            <div style={{borderRadius:12,overflow:"hidden",border:"1px solid rgba(255,210,52,0.18)"}}>
              <img loading="lazy" decoding="async" src={IMGS.bkfcStats} alt="BKFC Stats" style={{width:"100%",display:"block"}}/>
            </div>
            <div style={{borderRadius:12,overflow:"hidden",border:"1px solid rgba(255,210,52,0.18)"}}>
              <img loading="lazy" decoding="async" src={IMGS.mcgregor} alt="Conor McGregor" style={{width:"100%",display:"block"}}/>
            </div>
            <div style={{padding:"1rem",borderRadius:8,border:"1px solid rgba(255,210,52,0.28)",background:"rgba(255,210,52,0.05)"}}>
              <p style={{color:"rgba(255,255,255,0.72)",fontSize:"0.79rem",lineHeight:1.65,margin:0,fontStyle:"italic"}}>
                "McGregor's inclusion creates what many collectors believe could become the biggest chase card and highest-value modern trading card opportunity the combat sports hobby has ever seen."
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

// ── Oh YAAS Demo Site ──────────────────────────────────────────
function OhYAASDemo() {
  const {ref,inView} = useReveal();
  return (
    <section id="demo" style={{padding:"7rem 2rem",background:"#050403",position:"relative",overflow:"hidden"}} ref={ref as React.Ref<HTMLElement>}>
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:"70vw",height:"70vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(255,210,52,0.06) 0%,transparent 65%)",pointerEvents:"none"}}/>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:1100,margin:"0 auto",position:"relative"}}>
        <motion.div variants={fadeUp} style={{textAlign:"center",marginBottom:"3rem"}}>
          <Label>Live Experience</Label><GoldDivider/>
          <Heading className="mt-4">Oh YAAS Demo Site</Heading>
          <p style={{color:"rgba(255,255,255,0.55)",fontSize:"1rem",lineHeight:1.75,maxWidth:560,margin:"1.25rem auto 0",fontFamily:"'Inter',sans-serif"}}>
            See the full collector experience in action — tap the image to explore the live Oh YAAS platform.
          </p>
        </motion.div>
        <motion.div variants={fadeUp}>
          <a href="https://my.ohyaas.com/collection" target="_blank" rel="noopener noreferrer"
            style={{display:"block",borderRadius:16,overflow:"hidden",border:"1px solid rgba(255,210,52,0.35)",boxShadow:"0 0 80px rgba(255,210,52,0.12)",position:"relative",cursor:"pointer",transition:"box-shadow 0.3s,transform 0.3s"}}
            onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.boxShadow="0 0 120px rgba(255,210,52,0.28)";(e.currentTarget as HTMLElement).style.transform="scale(1.01)";}}
            onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.boxShadow="0 0 80px rgba(255,210,52,0.12)";(e.currentTarget as HTMLElement).style.transform="scale(1)";}}>
            <img loading="lazy" decoding="async"
              src="/laptop-demo.png"
              alt="Oh YAAS Demo Site"
              style={{width:"100%",display:"block"}}/>
            {/* Gold overlay CTA on hover */}
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(8,7,6,0.45)",opacity:0,transition:"opacity 0.3s"}}
              onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.opacity="1";}}
              onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.opacity="0";}}>
              <div style={{padding:"0.85rem 2rem",border:"2px solid #FFD234",borderRadius:8,color:"#FFD234",fontFamily:"'Oswald',sans-serif",fontSize:"1rem",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",background:"rgba(8,7,6,0.7)"}}>
                Explore The Platform →
              </div>
            </div>
          </a>
          <div style={{marginTop:"1.25rem",textAlign:"center"}}>
            <a href="https://my.ohyaas.com/collection" target="_blank" rel="noopener noreferrer"
              style={{color:"#FFD234",fontFamily:"'JetBrains Mono',monospace",fontSize:"0.8rem",letterSpacing:"0.1em",textDecoration:"none",opacity:0.8}}>
              my.ohyaas.com/collection ↗
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── The Ask ────────────────────────────────────────────────────
function TheAsk() {
  const {ref,inView} = useReveal();
  const mobile = useIsMobile();
  return (
    <section id="ask" style={{padding:"8rem 2rem",background:"#050403",position:"relative",overflow:"hidden"}} ref={ref as React.Ref<HTMLElement>}>
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:"80vw",height:"80vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(255,210,52,0.055) 0%,transparent 65%)",pointerEvents:"none"}}/>
      <motion.div variants={stagger} initial="hidden" animate={inView?"visible":"hidden"}
        style={{maxWidth:960,margin:"0 auto",textAlign:"center",position:"relative"}}>
        <motion.div variants={fadeUp}>
          <Label>The Investment Opportunity</Label><GoldDivider/>
          <div style={{fontFamily:"'Oswald',sans-serif",fontWeight:700,fontSize:"clamp(4rem,12vw,8.5rem)",lineHeight:0.83,textTransform:"uppercase",letterSpacing:"-0.02em",margin:"2rem 0"}}>
            <div style={{color:"rgba(255,255,255,0.12)"}}>THE</div>
            <div style={{color:"#FFD234"}}>$1.75M</div>
            <div style={{color:"rgba(255,255,255,0.12)"}}>ASK</div>
          </div>
        </motion.div>
        <motion.div variants={fadeUp} style={{maxWidth:700,margin:"0 auto 3rem"}}>
          <p style={{color:"rgba(255,255,255,0.72)",lineHeight:1.9,fontSize:"1.05rem",marginBottom:"1.25rem"}}>
            Oh YAAS is offering one exclusive brand the opportunity to become the <strong style={{color:"#FFD234"}}>Founding Global Sponsor</strong> of the first-ever officially licensed BKFC trading card release.
          </p>
          <p style={{color:"rgba(255,255,255,0.58)",lineHeight:1.85,fontSize:"0.95rem"}}>
            For an investment of $1.75 million, your brand will receive unmatched visibility across all 17 million trading cards, premium placement on every card, inside every card experience, and throughout every AR activation. You also receive comprehensive real-time analytics, first-party consumer data ownership, and first right of refusal for next year's release.
          </p>
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"grid",gridTemplateColumns:mobile?"1fr":"repeat(3,1fr)",gap:"1.25rem",marginBottom:"3.5rem"}}>
          {[{icon:<Layers size={22} strokeWidth={1.5} color="#FFD234"/>,title:"17M+ Cards",desc:"Premium placement across every card in the Oh YAAS ecosystem"},{icon:<BarChart2 size={22} strokeWidth={1.5} color="#FFD234"/>,title:"Real-Time Data",desc:"First-party consumer data ownership + comprehensive back-end analytics"},{icon:<RefreshCw size={22} strokeWidth={1.5} color="#FFD234"/>,title:"Right of Refusal",desc:"First right of refusal for next year's release and category leadership"}].map(it=>(
            <div key={it.title} style={{padding:"1.75rem",borderRadius:12,border:"1px solid rgba(255,210,52,0.24)",background:"rgba(255,210,52,0.04)"}}>
              <div style={{fontSize:"2rem",marginBottom:"0.75rem"}}>{it.icon}</div>
              <div style={{fontFamily:"'Oswald',sans-serif",color:"#FFD234",fontSize:"1.05rem",textTransform:"uppercase",marginBottom:"0.5rem"}}>{it.title}</div>
              <p style={{color:"rgba(255,255,255,0.5)",fontSize:"0.84rem",lineHeight:1.6,margin:0}}>{it.desc}</p>
            </div>
          ))}
        </motion.div>
        <motion.div variants={fadeUp} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"1.25rem"}}>
          <GoldBtn href="mailto:scott@youareallstars.com">Become The Founding Sponsor</GoldBtn>
          <p style={{color:"rgba(255,255,255,0.3)",fontSize:"0.8rem",maxWidth:520,lineHeight:1.6,margin:0}}>
            This is more than a sponsorship — it's the opportunity to own the most visible position within a next-generation sports, collectibles, and commerce platform.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── Footer ─────────────────────────────────────────────────────
function Footer() {
  const mobile = useIsMobile();
  return (
    <footer style={{padding:"4rem 2rem 2.5rem",background:"#030302",borderTop:"1px solid rgba(255,210,52,0.1)"}}>
      <div style={{maxWidth:1200,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"2fr 1fr 1fr",gap:mobile?"2rem":"3rem",marginBottom:"3rem"}}>
          <div>
            <div style={{fontFamily:"'Oswald',sans-serif",fontSize:"1.8rem",color:"#FFD234",fontWeight:700,letterSpacing:"0.08em",marginBottom:"0.75rem"}}>OH YAAS™</div>
            <p style={{color:"rgba(255,255,255,0.4)",fontSize:"0.88rem",lineHeight:1.7,maxWidth:340,margin:0}}>
              The first Experiential Collectible™ — combining the tangible appeal of physical trading cards with the immersive power of digital experiences.
            </p>
          </div>
          <div>
            <div style={{fontFamily:"'Oswald',sans-serif",color:"rgba(255,255,255,0.28)",fontSize:"0.73rem",textTransform:"uppercase",letterSpacing:"0.16em",marginBottom:"1rem"}}>Sections</div>
            {NAV.map(item=>(
              <a key={item.href} href={item.href} style={{display:"block",color:"rgba(255,255,255,0.42)",fontSize:"0.84rem",marginBottom:"0.45rem",textDecoration:"none",transition:"color 0.2s"}}
                onMouseEnter={e=>(e.currentTarget.style.color="#FFD234")}
                onMouseLeave={e=>(e.currentTarget.style.color="rgba(255,255,255,0.42)")}>
                {item.label}
              </a>
            ))}
          </div>
          <div>
            <div style={{fontFamily:"'Oswald',sans-serif",color:"rgba(255,255,255,0.28)",fontSize:"0.73rem",textTransform:"uppercase",letterSpacing:"0.16em",marginBottom:"1rem"}}>Contact</div>
            <div style={{color:"rgba(255,255,255,0.65)",fontSize:"0.9rem",marginBottom:"0.35rem",fontWeight:600}}>Scott Terrell</div>
            <div style={{color:"rgba(255,255,255,0.38)",fontSize:"0.8rem",marginBottom:"0.3rem"}}>Founder &amp; President</div>
            <a href="tel:4699865873" style={{color:"#FFD234",fontSize:"0.84rem",display:"block",marginBottom:"0.3rem",textDecoration:"none"}}>469-986-5873</a>
            <a href="mailto:scott@youareallstars.com" style={{color:"#FFD234",fontSize:"0.8rem",textDecoration:"none"}}>scott@youareallstars.com</a>
          </div>
        </div>
        <GoldDivider/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:"1.5rem",flexWrap:"wrap",gap:"1rem"}}>
          <p style={{color:"rgba(255,255,255,0.22)",fontSize:"0.73rem",margin:0}}>© 2026 Oh YAAS Inc. All rights reserved. Exclusively prepared for ShipStation Global.</p>
          <p style={{color:"rgba(255,255,255,0.18)",fontSize:"0.68rem",margin:0}}>Proprietary &amp; Confidential — Redistribution without prior consent is not permitted.</p>
        </div>
      </div>
    </footer>
  );
}

// ── Floating Audio Player ──────────────────────────────────────
function FloatingPlayer({titleRef}:{titleRef:React.RefObject<HTMLDivElement>}) {
  const audioRef = useRef<HTMLAudioElement|null>(null);
  const ctxRef = useRef<AudioContext|null>(null);
  const analyserRef = useRef<AnalyserNode|null>(null);
  const rafRef = useRef<number>(0);
  const mutedRef = useRef(false);
  const volumeRef = useRef(0.5);
  const [playing,setPlaying] = useState(false);
  const [muted,setMuted] = useState(false);
  const [volume,setVolume] = useState(0.5);
  const [pulse,setPulse] = useState(0);

  useEffect(()=>{
    // Create audio element (not appended to DOM — iOS requires it in DOM for some Safari versions)
    const audio = document.createElement("audio");
    // NOTE: do NOT set crossOrigin — it blocks loading when CDN lacks CORS headers
    audio.src = MUSIC_URL;
    audio.loop = true;
    audio.volume = 0.5;
    audio.preload = "auto"; // buffer immediately so play() starts fast
    document.body.appendChild(audio); // iOS Safari needs audio in DOM
    audioRef.current = audio;

    // Auto-duck when any unmuted video starts playing
    const onMediaPlay = (e:Event)=>{
      const el = e.target as HTMLVideoElement;
      if (el.tagName==="VIDEO" && !el.muted && audioRef.current && !audioRef.current.paused) {
        audioRef.current.volume = 0;
      }
    };
    const onMediaPause = (e:Event)=>{
      const el = e.target as HTMLVideoElement;
      if (el.tagName==="VIDEO" && audioRef.current && !mutedRef.current) {
        audioRef.current.volume = volumeRef.current;
      }
    };
    document.addEventListener("play", onMediaPlay, true);
    document.addEventListener("pause", onMediaPause, true);
    document.addEventListener("ended", onMediaPause, true);

    return ()=>{
      cancelAnimationFrame(rafRef.current);
      audio.pause();
      audio.src = "";
      if (audio.parentNode) audio.parentNode.removeChild(audio);
      document.removeEventListener("play", onMediaPlay, true);
      document.removeEventListener("pause", onMediaPause, true);
      document.removeEventListener("ended", onMediaPause, true);
      ctxRef.current?.close();
    };
  },[]);

  // Pulse dots ticker
  useEffect(()=>{
    if (!playing) return;
    const t = setInterval(()=>setPulse(p=>(p+1)%4), 320);
    return ()=>clearInterval(t);
  },[playing]);

  const initCtx = ()=>{
    if (ctxRef.current) return;
    try {
      const AC = ((window as any).AudioContext||(window as any).webkitAudioContext) as typeof AudioContext;
      const ctx = new AC();
      // createMediaElementSource requires CORS headers — skip it to avoid blocking audio
      // Beat animation uses time-based sine wave instead (works everywhere, no CORS needed)
      ctxRef.current = ctx;
    } catch(_){ /* AudioContext not available — time-based fallback will be used */ }
  };

  const runBeatLoop = ()=>{
    cancelAnimationFrame(rafRef.current);
    const analyser = analyserRef.current;
    const data = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const loop = ()=>{
      let norm = 0;
      if (analyser && data) {
        analyser.getByteFrequencyData(data);
        // Focus on bass bins (kick drum range)
        const bass = (data[1]+data[2]+data[3]+data[4]+data[5]+data[6])/6;
        norm = bass/255;
      } else {
        // Time-based fallback at ~124 BPM for when AudioContext is unavailable
        norm = Math.max(0,(Math.sin(Date.now()/483*Math.PI)+0.2)/1.2);
      }
      const scale = 1 + norm*0.075;
      const glow = norm*28;
      if (titleRef.current) {
        titleRef.current.style.transform = `scale(${scale.toFixed(3)})`;
        titleRef.current.style.textShadow = glow>4
          ? `0 0 ${Math.round(glow)}px rgba(255,210,52,${(norm*0.9).toFixed(2)}),0 0 ${Math.round(glow*2.2)}px rgba(255,210,52,${(norm*0.35).toFixed(2)})`
          : "none";
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const stopBeatLoop = ()=>{
    cancelAnimationFrame(rafRef.current);
    if (titleRef.current) {
      titleRef.current.style.transform = "";
      titleRef.current.style.textShadow = "";
    }
  };

  const handlePlayPause = ()=>{
    const audio = audioRef.current!;
    if (!playing) {
      initCtx();
      // iOS/Safari CRITICAL: audio.play() must be called synchronously inside the
      // user-gesture handler — any await before it drops the activation context
      if (ctxRef.current?.state==="suspended") {
        ctxRef.current.resume().catch(()=>{});
      }
      const p = audio.play();
      if (p !== undefined) {
        p.then(()=>{
          setPlaying(true);
          runBeatLoop();
        }).catch(err=>{
          console.warn("Audio play failed:", err);
        });
      } else {
        // Older Safari returns undefined
        setPlaying(true);
        runBeatLoop();
      }
    } else {
      audio.pause();
      setPlaying(false);
      stopBeatLoop();
    }
  };

  const handleMute = ()=>{
    const audio = audioRef.current!;
    const next = !muted;
    audio.muted = next;
    mutedRef.current = next;
    setMuted(next);
  };

  const handleVolume = (e:React.ChangeEvent<HTMLInputElement>)=>{
    const val = parseFloat(e.target.value);
    volumeRef.current = val;
    setVolume(val);
    if (audioRef.current){
      audioRef.current.volume = val;
      const nowMuted = val===0;
      audioRef.current.muted = nowMuted;
      mutedRef.current = nowMuted;
      setMuted(nowMuted);
    }
  };

  return (
    <div style={{position:"fixed",bottom:20,left:20,zIndex:999,display:"flex",alignItems:"center",gap:10,padding:"9px 14px 9px 11px",
      background:"rgba(6,5,4,0.92)",border:"1px solid rgba(255,210,52,0.32)",borderRadius:50,
      backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
      boxShadow:"0 8px 36px rgba(0,0,0,0.6),0 0 0 1px rgba(255,210,52,0.07)"}}>

      {/* Play / Pause */}
      <button onClick={handlePlayPause}
        style={{width:36,height:36,borderRadius:"50%",flexShrink:0,cursor:"pointer",padding:0,
          background:playing?"linear-gradient(135deg,#FFD234,#AA8800)":"rgba(255,210,52,0.1)",
          border:"1.5px solid rgba(255,210,52,0.5)",display:"flex",alignItems:"center",justifyContent:"center",
          transition:"all 0.2s"}}>
        <span style={{fontSize:"0.82rem",color:playing?"#000":"#FFD234",lineHeight:1}}>
          {playing ? "⏸" : "▶"}
        </span>
      </button>

      {/* Track info + beat dots */}
      <div style={{display:"flex",flexDirection:"column",lineHeight:1.25,minWidth:96}}>
        <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:"0.53rem",letterSpacing:"0.14em",
          color:playing?"rgba(255,210,52,0.75)":"rgba(255,255,255,0.3)"}}>
          {playing ? "NOW PLAYING" : "TAP TO PLAY"}
        </span>
        <span style={{fontFamily:"'Oswald',sans-serif",fontSize:"0.72rem",textTransform:"uppercase",
          letterSpacing:"0.06em",color:playing?"#fff":"rgba(255,255,255,0.4)",transition:"color 0.3s"}}>
          TRADING CARDS
        </span>
        {playing && (
          <div style={{display:"flex",gap:3,marginTop:2}}>
            {[0,1,2,3].map(i=>(
              <div key={i} style={{width:4,height:4,borderRadius:"50%",background:"#FFD234",
                transition:"opacity 0.18s",opacity:pulse===i?1:0.22}}/>
            ))}
          </div>
        )}
      </div>

      {/* Volume slider */}
      <input type="range" min={0} max={1} step={0.02} value={muted?0:volume} onChange={handleVolume}
        style={{width:68,cursor:"pointer",accentColor:"#FFD234",opacity:0.85}} />

      {/* Mute toggle */}
      <button onClick={handleMute}
        style={{width:28,height:28,borderRadius:"50%",flexShrink:0,cursor:"pointer",padding:0,
          background:"transparent",border:"1px solid rgba(255,210,52,0.22)",
          display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
        <span style={{fontSize:"0.78rem",opacity:muted?0.3:0.75}}>{muted?"🔇":"🔊"}</span>
      </button>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────
const Index = () => {
  const [scrolled,setScrolled] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  useEffect(()=>{
    const h=()=>setScrolled(window.scrollY>60);
    window.addEventListener("scroll",h,{passive:true});
    return ()=>window.removeEventListener("scroll",h);
  },[]);
  return (
    <div style={{background:"#080706",minHeight:"100vh"}}>
      <Navbar scrolled={scrolled}/>
      <Hero titleRef={titleRef}/>
      <Overview/>
      <HowItWorks/>
      <OldVsNew/>
      <Marketing/>
      <Sponsorship/>
      <CardGallery/>
      <Analytics/>
      <FanEngagement/>
      <BKFCStats/>
      <OhYAASDemo/>
      <TheAsk/>
      <Footer/>
      <FloatingPlayer titleRef={titleRef}/>
    </div>
  );
};

export default Index;
